import type { BrowserViewport } from "@cmmd-forge/contracts";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { cn } from "~/lib/utils";
import {
  capturePointToViewportPoint,
  resolveBrowserCaptureLayout,
  type BrowserCaptureLayout,
} from "./BrowserCanvas.logic";
import { mapBrowserKeyboardEvent } from "./InteractiveBrowserSurface.logic";

export interface BrowserSurfacePointerCommand {
  readonly type: "clickPoint" | "wheel";
  readonly x: number;
  readonly y: number;
  readonly deltaX?: number;
  readonly deltaY?: number;
  readonly button?: "left" | "middle" | "right";
  readonly clickCount?: number;
}

export interface BrowserSurfaceKeyboardCommand {
  readonly type: "pressKey" | "typeText";
  readonly key?: string;
  readonly text?: string;
  readonly pressEnter?: boolean;
}

interface BrowserLiveViewSurfaceProps {
  readonly url: string | null;
  readonly tabId: string | null;
  readonly frameDataUrl: string | undefined;
  readonly viewport: BrowserViewport | undefined;
  readonly interactionEnabled: boolean;
  readonly selectionEnabled: boolean;
  readonly onPointerCommand: (command: BrowserSurfacePointerCommand) => void;
  readonly onKeyboardCommand: (command: BrowserSurfaceKeyboardCommand) => void;
  readonly onContainerResize?: (size: { width: number; height: number }) => void;
}

export function mapBrowserLiveViewPointerToViewport(input: {
  readonly event: { readonly clientX: number; readonly clientY: number };
  readonly bounds: DOMRect;
  readonly layout: BrowserCaptureLayout | null;
  readonly viewport: BrowserViewport | undefined;
}): { readonly x: number; readonly y: number } | null {
  const capturePoint = {
    x: input.event.clientX - input.bounds.left,
    y: input.event.clientY - input.bounds.top,
  };
  if (input.layout && input.viewport) {
    return capturePointToViewportPoint({
      point: capturePoint,
      layout: input.layout,
      viewport: input.viewport,
    });
  }
  return capturePoint;
}

export function BrowserLiveViewSurface(props: BrowserLiveViewSurfaceProps) {
  const {
    frameDataUrl,
    interactionEnabled,
    onContainerResize,
    onKeyboardCommand,
    onPointerCommand,
    selectionEnabled,
    tabId,
    url,
    viewport,
  } = props;
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [surfaceSize, setSurfaceSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    const updateSize = () => {
      const bounds = surface.getBoundingClientRect();
      const size = { width: bounds.width, height: bounds.height };
      setSurfaceSize(size);
      onContainerResize?.(size);
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [onContainerResize]);

  const captureLayout = useMemo(
    () =>
      frameDataUrl
        ? resolveBrowserCaptureLayout({
            containerWidth: surfaceSize.width,
            containerHeight: surfaceSize.height,
            viewport,
          })
        : null,
    [frameDataUrl, surfaceSize.height, surfaceSize.width, viewport],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    const pixelRatio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(surfaceSize.width));
    const height = Math.max(1, Math.round(surfaceSize.height));
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, width, height);
    if (!frameDataUrl || !captureLayout) {
      return;
    }

    let cancelled = false;
    const image = new Image();
    const handleLoad = () => {
      if (cancelled) {
        return;
      }
      context.clearRect(0, 0, width, height);
      context.drawImage(
        image,
        captureLayout.left,
        captureLayout.top,
        captureLayout.width,
        captureLayout.height,
      );
    };
    const handleError = () => {
      if (!cancelled) {
        console.warn("BrowserLiveViewSurface: failed to decode screencast frame");
      }
    };
    image.addEventListener("load", handleLoad);
    image.addEventListener("error", handleError);
    image.src = frameDataUrl;
    return () => {
      cancelled = true;
      image.removeEventListener("load", handleLoad);
      image.removeEventListener("error", handleError);
    };
  }, [captureLayout, frameDataUrl, surfaceSize.height, surfaceSize.width]);

  const resolveEventPoint = useCallback(
    (event: ReactPointerEvent<HTMLDivElement> | ReactWheelEvent<HTMLDivElement>) => {
      const bounds = surfaceRef.current?.getBoundingClientRect();
      if (!bounds) {
        return null;
      }
      return mapBrowserLiveViewPointerToViewport({
        event,
        bounds,
        layout: captureLayout,
        viewport,
      });
    },
    [captureLayout, viewport],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      event.currentTarget.focus();
    },
    [interactionEnabled, selectionEnabled, tabId],
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      const point = resolveEventPoint(event);
      if (!point) {
        return;
      }
      event.preventDefault();
      onPointerCommand({
        type: "clickPoint",
        x: point.x,
        y: point.y,
        button: event.button === 2 ? "right" : event.button === 1 ? "middle" : "left",
        ...(event.detail > 1 ? { clickCount: event.detail } : {}),
      });
    },
    [interactionEnabled, onPointerCommand, resolveEventPoint, selectionEnabled, tabId],
  );

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      const point = resolveEventPoint(event);
      if (!point) {
        return;
      }
      event.preventDefault();
      onPointerCommand({
        type: "wheel",
        x: point.x,
        y: point.y,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
      });
    },
    [interactionEnabled, onPointerCommand, resolveEventPoint, selectionEnabled, tabId],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      const command = mapBrowserKeyboardEvent(event);
      if (command) {
        event.preventDefault();
        onKeyboardCommand(command);
      }
    },
    [interactionEnabled, onKeyboardCommand, selectionEnabled, tabId],
  );

  return (
    <div
      ref={surfaceRef}
      data-testid="browser-live-view-surface"
      className={cn(
        "relative h-full w-full overflow-hidden bg-muted/20 outline-none",
        interactionEnabled && !selectionEnabled ? "cursor-default" : "cursor-auto",
      )}
      tabIndex={interactionEnabled ? 0 : -1}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
    >
      <canvas
        ref={canvasRef}
        data-testid="browser-live-view-canvas"
        role="img"
        aria-label={url ? `Browser live view of ${url}` : "Browser live view"}
        className="absolute inset-0 h-full w-full"
      />
      {!frameDataUrl ? (
        <div className="pointer-events-none absolute inset-4 rounded-md border border-dashed border-border/60 bg-background/40" />
      ) : null}
    </div>
  );
}
